/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import isEmpty from 'lodash.isempty';
import {useRouter} from 'next/compat/router';
import {postInitializationCompleteSelector} from '../store/selectors/login';
import {setQueryParams} from './state';

/**
 * @hook useQueryParamProvider
 *
 * Provides a utility hook for processing url query params passed to certain pages. This hook will
 * redirect app to appropriate page based on the params
 *
 * ```typescript
 *
 *   import {useQueryParamProvider} from '@ion/core/src/hooks/useQueryParamProvider';
 *
 *   const SomeComponent = () => {
 *     useQueryParamProvider(handleQueryParams, isAuthenticated);
 *   }
 *
 * ```
 */
export const useQueryParamProvider = (handleQueryParams: Function, isAuthenticated: boolean) => {
  const [refresh, setRefresh]      = useState(false);
  const {pathname, query}          = useRouter();
  const savedQuery                 = useSelector(({query}) => query.queryParams);
  const dispatch                   = useDispatch();
  const postInitializationComplete = useSelector(postInitializationCompleteSelector);

  useEffect(() => {
    if (!isEmpty(query)) {
      dispatch(setQueryParams({pathname, queryParams: query}));
      setRefresh(true);
    }
  }, [pathname, query]);

  useEffect(() => {
    if (!isEmpty(savedQuery) && refresh && isAuthenticated && postInitializationComplete) {
      dispatch(handleQueryParams());
      setRefresh(false);
    }
  }, [savedQuery, refresh, isAuthenticated, postInitializationComplete]);
};
